﻿import {isNullOrEmpty, StringNamedValue, translation} from "@2d-front-monorepo/core";
import {Button, OptionList, OptionListEntry, Select} from "@2d-front-monorepo/design-library";
import {AttachmentType, EntityDocument, EnumLists} from "@2d-front-monorepo/permits-business";
import {PermitTranslations} from "@2d-front-monorepo/shared-translations";
import {JSX, useCallback, useContext, useMemo, useState} from "react";
import {useFilteredItems} from "./FilteredItemsProvider";
import {FilterManager, useFilter} from "./FilterManager";
import {FilterEntry, FilterPopoverContext, FiltersComponent} from "./FiltersComponent";

function DocumentTypeFilter(): JSX.Element {
	const popover = useContext(FilterPopoverContext);
	const [documentType, setDocumentType] = useState<AttachmentType>(AttachmentType.Undefined);

	const entries = useMemo(() => {
		const result: OptionListEntry[] = [];
		for (const {label, value} of EnumLists.attachmentType()) {
			result.push({
				label: label,
				value: value.toString(10)
			});
		}

		return result;
	}, []);

	const filterFn = useCallback((items: EntityDocument[]) =>
		documentType === AttachmentType.Undefined
			? items
			: items.filter(item => item.attachment.type === documentType), [documentType]);

	useFilter("document-type", filterFn);

	return (
		<div className="layout vertical padded inline">
			<Select label={translation(PermitTranslations.documents_page__filters__document_type_label)}
			        name="tariff"
			        items={entries}
			        placeholder={translation(PermitTranslations.documents_page__filters__document_type_placeholder)}
			        onChange={e => {
				        const selected = (e.target.selectedIndex ?? 0) - 1;
				        setDocumentType((selected === -1 ? AttachmentType.Undefined : parseInt(entries[selected].value, 10) as AttachmentType));
			        }}/>

			<div className="form__buttons">
				<Button kind="primary" onClick={() => popover?.close()}>
					{translation(PermitTranslations.done)}
				</Button>
			</div>
		</div>
	);
}

function CategoryFilter(): JSX.Element {
	const popover = useContext(FilterPopoverContext);
	const [entityType, setEntityType] = useState<string | undefined>(undefined);

	const entityTypes = useMemo<StringNamedValue[]>(() => {
		return [
			{label: translation("entity__type--residency"), value: "residency"},
			{label: translation("entity__type--driver"), value: "driver"},
			{label: translation("entity__type--vehicle"), value: "vehicle"},
			{label: translation("entity__type--holder"), value: "holder"},
			{label: translation("entity__type--permit"), value: "permit"}
		]
	}, []);

	const filterFn = useCallback((items: EntityDocument[]) =>
		isNullOrEmpty(entityType)
			? items
			: items.filter(item => item.entityType === entityType), [entityType]);

	useFilter("category", filterFn);

	return (
		<div className="layout vertical padded inline">
			<Select label={translation(PermitTranslations.documents_page__filters__category_label)}
			        name="tariff"
			        items={entityTypes}
			        placeholder={translation(PermitTranslations.documents_page__filters__category_placeholder)}
			        onChange={e => {
				        const selected = (e.target.selectedIndex ?? 0) - 1;
				        setEntityType((selected === -1 ? undefined : entityTypes[selected].value));
			        }}/>

			<div className="form__buttons">
				<Button kind="primary" onClick={() => popover?.close()}>
					{translation(PermitTranslations.done)}
				</Button>
			</div>
		</div>
	);
}

function StatusFilter(): JSX.Element {
	const popover = useContext(FilterPopoverContext);
	const [status, setStatus] = useState<string[]>([]);
	const filterFn = useCallback((items: EntityDocument[]) => {
			return (status.length > 0 ? items.filter(item => status.includes(item.attachment.status.toString(10))) : items);
		}
		, [status]);

	const entries = useMemo(() => {
		const result: OptionListEntry[] = [];
		for (const {label, value} of EnumLists.documentStatusType()) {
			result.push({
				label: label,
				value: value.toString(10)
			});
		}

		return result;
	}, []);

	useFilter("status", filterFn);

	return (
		<div className="layout vertical padded inline">
			<OptionList name="status" values={status} items={entries} multiple={true} onChange={selected => {
				setStatus(selected.map(x => entries[x].value))
			}}/>

			<div className="form__buttons">
				<Button kind="primary" onClick={() => popover?.close()}>
					{translation(PermitTranslations.done)}
				</Button>
			</div>
		</div>);
}

export function DocumentsFilterComponent(): JSX.Element {
	const {source, setFiltered} = useFilteredItems<EntityDocument>();

	return (
		<FilterManager source={source} onFiltered={setFiltered}>
			<FiltersComponent title={translation(PermitTranslations.documents_page__filters)}>
				<FilterEntry title={translation(PermitTranslations.documents_page__filters__document_type_title)}
				             label={translation(PermitTranslations.documents_page__filters__document_type_label)}>
					<DocumentTypeFilter/>
				</FilterEntry>

				<FilterEntry title={translation(PermitTranslations.documents_page__filters__category_title)}
				             label={translation(PermitTranslations.documents_page__filters__category_label)}>
					<CategoryFilter/>
				</FilterEntry>

				<FilterEntry title={translation(PermitTranslations.documents_page__filters__status_title)}
				             label={translation(PermitTranslations.documents_page__filters__status_label)}>
					<StatusFilter/>
				</FilterEntry>
			</FiltersComponent>
		</FilterManager>
	)
}
